import React, { useEffect } from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { Plus, X } from 'lucide-react';
import './JobArchitectureGrid.css';
import EditableCard from './EditableCard';
import type { Card as CardType, JobFunctionGroup } from '../types';

interface JobArchitectureGridProps {
  jobFunctionGroups: JobFunctionGroup[];
  levels: string[];
  cards: CardType[];
  onRemoveCard?: (id: string) => void;
  onEditCard?: (id: string, newContent: string) => void;
  onEditSubJob?: (id: string, subJobIndex: number, newContent: string) => void;
  onRemoveSubJob?: (id: string, subJobIndex: number) => void;
  onAddSubJob?: (id: string) => void;
  onRowHeightsCalculated?: (rowHeights: number[]) => void;
  onAddFunctionToGroup?: (groupId: string) => void;
  onDeleteFunction?: (functionId: string) => void;
  onDeleteGroup?: (groupId: string) => void;
}

const JobArchitectureGrid: React.FC<JobArchitectureGridProps> = ({
  jobFunctionGroups,
  levels,
  cards,
  onRemoveCard,
  onEditCard,
  onEditSubJob,
  onRemoveSubJob,
  onAddSubJob,
  onRowHeightsCalculated,
  onAddFunctionToGroup,
  onDeleteFunction,
  onDeleteGroup,
}) => {
  const rows = levels.length;

  // Build a combined list: include empty groups as placeholders + actual functions
  const allColumns: Array<{ type: 'group' | 'function'; group?: JobFunctionGroup; func?: any }> = [];

  jobFunctionGroups.forEach(group => {
    if (group.functions.length === 0) {
      // Empty group - add as placeholder column
      allColumns.push({ type: 'group', group });
    } else {
      // Group with functions - add each function
      group.functions.forEach(func => {
        allColumns.push({ type: 'function', func, group });
      });
    }
  });

  // Get ordered list of functions from groups (naturally ordered)
  const orderedFunctions = jobFunctionGroups.flatMap(group => group.functions);

  // Helper function to find card at specific position
  const getCardAtPosition = (functionId: string, row: number): CardType | undefined => {
    return cards.find(card => card.functionId === functionId && card.rowIndex === row);
  };

  // Calculate the number of jobs in each cell (1 for main + subjobs length)
  const getJobCountForCell = (card: CardType | undefined): number => {
    if (!card) return 0;
    return 1 + (card.subJobs?.length || 0);
  };

  // Calculate max jobs per row (level) - determines row height
  const getMaxJobsPerRow = (): number[] => {
    const maxJobsPerRow: number[] = [];
    for (let row = 0; row < rows; row++) {
      let maxJobs = 1; // Default minimum of 1
      for (const func of orderedFunctions) {
        const card = getCardAtPosition(func.id, row);
        const jobCount = getJobCountForCell(card);
        if (jobCount > maxJobs) {
          maxJobs = jobCount;
        }
      }
      maxJobsPerRow.push(maxJobs);
    }
    return maxJobsPerRow;
  };

  const maxJobsPerRow = getMaxJobsPerRow();

  // Build grid-template-rows based on job counts (30px per job)
  const gridTemplateRows = maxJobsPerRow.map(count => `${count * 30}px`).join(' ');

  // Notify parent component of row heights whenever they change
  useEffect(() => {
    if (onRowHeightsCalculated) {
      onRowHeightsCalculated(maxJobsPerRow);
    }
  }, [JSON.stringify(maxJobsPerRow), onRowHeightsCalculated]);

  return (
    <div className="job-arch-grid-wrapper">
      {/* Job Function Group headers row */}
      <div
        className="job-function-group-headers"
        style={{
          gridTemplateColumns: `repeat(${allColumns.length}, 1fr)`
        }}
      >
        {jobFunctionGroups.map((group) => {
          const colspan = group.functions.length === 0 ? 1 : group.functions.length;
          return (
            <div
              key={`group-${group.id}`}
              className={`job-function-group-header ${group.functions.length === 0 ? 'empty-group' : ''}`}
              style={{ gridColumn: `span ${colspan}` }}
            >
              <span className="group-label">{group.label}</span>
              <button
                className="group-add-button"
                onClick={() => onAddFunctionToGroup?.(group.id)}
                title="Add job function to this group"
              >
                <Plus size={14} />
              </button>
              <button
                className="group-delete-button"
                onClick={() => onDeleteGroup?.(group.id)}
                title="Delete this job function group"
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Job Function headers row */}
      <div
        className="job-arch-grid-headers"
        style={{
          gridTemplateColumns: `repeat(${allColumns.length}, 1fr)`
        }}
      >
        {allColumns.map((col) => {
          if (col.type === 'group') {
            // Empty group placeholder
            return (
              <div key={`empty-${col.group!.id}`} className="job-arch-column-header empty-function-placeholder">
                <span className="function-label">(Empty - add function)</span>
              </div>
            );
          } else {
            // Regular function
            return (
              <div key={`header-${col.func.id}`} className="job-arch-column-header">
                <span className="function-label">{col.func.label}</span>
                {onDeleteFunction && (
                  <button
                    className="function-delete-button"
                    onClick={() => onDeleteFunction(col.func.id)}
                    title="Delete this job function"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            );
          }
        })}
      </div>

      {/* Main grid without row labels (Levels are in static column) */}
      <div
        className="job-arch-grid-container"
        style={{
          gridTemplateColumns: `repeat(${allColumns.length}, 1fr)`,
          gridTemplateRows: gridTemplateRows
        }}
      >
          {levels.flatMap((level, row) => {
            const isEmptyLevel = level.startsWith('(Empty - add level');

            return allColumns.map((col) => {
              if (col.type === 'group' || isEmptyLevel) {
                // Empty group or empty level - render disabled cell
                return (
                  <div
                    key={`empty-cell-${col.type === 'group' ? col.group!.id : col.func.id}-${row}`}
                    className="job-arch-grid-cell empty-group-cell"
                  >
                    {/* Non-droppable disabled cell */}
                  </div>
                );
              } else {
                // Regular function cell
                const card = getCardAtPosition(col.func.id, row);
                const droppableId = `${col.func.id}:${row}`;

                return (
                  <Droppable key={droppableId} droppableId={droppableId}>
                    {(droppableProvided, droppableSnapshot) => (
                      <div
                        ref={droppableProvided.innerRef}
                        {...droppableProvided.droppableProps}
                        className={`job-arch-grid-cell ${droppableSnapshot.isDraggingOver ? 'dragging-over' : ''}`}
                        data-function-id={col.func.id}
                        data-row={row}
                      >
                        {card ? (
                          <Draggable
                            key={card.id}
                            draggableId={card.id}
                            index={0}
                          >
                            {(draggableProvided, draggableSnapshot) => (
                              <div
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                                {...draggableProvided.dragHandleProps}
                                className="card-wrapper"
                                style={{
                                  ...draggableProvided.draggableProps.style,
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  zIndex: draggableSnapshot.isDragging ? 1000 : 1,
                                }}
                              >
                                <EditableCard
                                  id={card.id}
                                  content={card.content}
                                  subJobs={card.subJobs}
                                  isPreview={card.isPreview}
                                  onRemove={onRemoveCard}
                                  onEdit={onEditCard}
                                  onEditSubJob={onEditSubJob}
                                  onRemoveSubJob={onRemoveSubJob}
                                  onAddSubJob={onAddSubJob}
                                />
                              </div>
                            )}
                          </Draggable>
                        ) : null}
                        {droppableProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                );
              }
            });
          })}
      </div>
    </div>
  );
};

export default JobArchitectureGrid;
